// Push-notification subscription UI — single "Enable / Disable" pill button.
// Used in the Live Room (so students opt-in next to the alert ticker)
// and in the Profile panel.
//
// SW must already be registered (see /service-worker.js + index.js).
import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const T = { gold: "#C9A227", goldLight: "#E8B94F", green: "#22C55E", red: "#FF4444", border: "#2a2e36", mute: "#888" };

const isSupported = () =>
  typeof window !== "undefined"
  && "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  return await navigator.serviceWorker.getRegistration();
}

export default function PushNotifications({ compact = false }) {
  const [supported] = useState(() => isSupported());
  const [permission, setPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!supported) return;
    (async () => {
      const reg = await getRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    })();
  }, [supported]);

  const enable = async () => {
    setBusy(true); setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setError("Permission denied — enable in your browser settings to use push.");
        setBusy(false);
        return;
      }
      const token = localStorage.getItem("flexx_access_token");
      if (!token) throw new Error("Sign in first");

      const keyRes = await axios.get(`${API}/push/vapid-public-key`, { headers: { Authorization: `Bearer ${token}` } });
      const reg = await getRegistration();
      if (!reg) throw new Error("Service worker not active — refresh the page");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyRes.data.key),
      });
      const subJson = sub.toJSON();
      await axios.post(`${API}/push/subscribe`,
        { endpoint: subJson.endpoint, keys: subJson.keys, user_agent: navigator.userAgent },
        { headers: { Authorization: `Bearer ${token}` } });
      setSubscribed(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not enable");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true); setError(null);
    try {
      const reg = await getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        const json = sub.toJSON();
        const token = localStorage.getItem("flexx_access_token");
        try {
          await axios.post(`${API}/push/unsubscribe`,
            { endpoint: json.endpoint, keys: json.keys || {} },
            { headers: { Authorization: `Bearer ${token}` } });
        } catch (_) { /* server may already be cleared */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (e) {
      setError(e.message || "Could not disable");
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div data-testid="push-unsupported" style={{ ...wrap(compact), color: T.mute, fontFamily: "monospace", fontSize: 11 }}>
        🔕 Push not supported on this browser. (Try Chrome / Firefox / Edge or install FLEXX as a PWA.)
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div data-testid="push-denied" style={{ ...wrap(compact), color: T.red, fontFamily: "monospace", fontSize: 11 }}>
        🔕 Notifications blocked — re-enable them in your browser site settings.
      </div>
    );
  }

  return (
    <div data-testid="push-notifications" style={wrap(compact)}>
      {!compact && (
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#EDE8DC", fontWeight: 700, fontSize: 13 }}>
            {subscribed ? "✅ Live alerts ON" : "🔔 Live alerts"}
          </div>
          <div style={{ color: T.mute, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
            {subscribed
              ? "You'll get a notification the moment the room goes live."
              : "Get a phone notification when FLEXX goes live or admin pushes an alert."}
          </div>
        </div>
      )}
      <button data-testid={subscribed ? "push-disable-btn" : "push-enable-btn"}
        onClick={subscribed ? disable : enable} disabled={busy}
        style={subscribed ? ghostBtn : primaryBtn}>
        {busy ? "…" : (subscribed ? "Disable" : "Enable Push")}
      </button>
      {error && (
        <div data-testid="push-error" style={{ color: T.red, fontSize: 11, fontFamily: "monospace", flexBasis: "100%" }}>{error}</div>
      )}
    </div>
  );
}

const wrap = (compact) => ({
  display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
  padding: compact ? "6px 10px" : "10px 12px",
  background: "#0d1218", border: `1px solid ${T.border}`, borderRadius: 8,
});
const primaryBtn = {
  padding: "8px 14px", borderRadius: 6,
  background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
  color: "#0a0a0a", border: "none", cursor: "pointer",
  fontWeight: 800, fontFamily: "monospace", letterSpacing: 1, fontSize: 11, textTransform: "uppercase",
};
const ghostBtn = {
  padding: "8px 14px", borderRadius: 6,
  background: "transparent", border: `1px solid ${T.border}`, color: T.mute,
  cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
};
